import { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Cpu, Compass, CreditCard, Shield, Palette, Car, Camera, Scale, Zap, Lock, ChefHat, Wifi, Waves, ShieldCheck, Megaphone, Building2 } from "lucide-react";
import { CoralPin } from "@/components/branding/CoralPin";
import { useTranslation } from "react-i18next";

import { tAuto } from "@/lib/tAuto";
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  cpu: Cpu, sparkles: CoralPin, compass: Compass, "credit-card": CreditCard,
  shield: Shield, palette: Palette, car: Car, camera: Camera, scale: Scale,
  zap: Zap, lock: Lock, "chef-hat": ChefHat, wifi: Wifi, waves: Waves,
  "shield-check": ShieldCheck, megaphone: Megaphone, building: Building2,
};

interface Partner {
  id: string;
  name: string;
  description: string;
  logo_url: string | null;
  logo_icon: string | null;
  display_order: number;
}

const PartnerCard = ({ partner }: { partner: Partner }) => {
  const Icon = ICON_MAP[partner.logo_icon || "building"] || Building2;

  return (
    <motion.div
      className="flex-shrink-0 w-[210px] group"
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      <div className="relative h-full rounded-2xl border border-black/10 bg-white/70 backdrop-blur-xl p-5 overflow-hidden transition-all duration-300 group-hover:border-black/15 group-hover:bg-white/85 group-hover:shadow-[0_14px_55px_-25px_rgba(0,0,0,0.22)]">
        {/* Glow on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-[hsl(var(--bera-gold))]/25 rounded-full blur-2xl" />
        </div>

        {/* Logo */}
        <div className="relative z-10 mb-3">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.name}
              className="w-10 h-10 rounded-xl object-contain bg-black/5 p-1.5"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-black/5 to-black/0 border border-black/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[hsl(var(--bera-earth))]/70" />
            </div>
          )}
        </div>

        {/* Text */}
        <div className="relative z-10">
          <h3 className="text-[hsl(var(--bera-earth))] font-semibold text-[13px] leading-tight mb-1 truncate tracking-tight">
            {partner.name}
          </h3>
          <p className="text-[hsl(var(--bera-earth))]/55 text-xs leading-snug line-clamp-1">
            {partner.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Parceiros padrão - Sanducha e WiFi Express
const DEFAULT_PARTNERS: Partner[] = [
  {
    id: "sanducha",
    name: "Sanducha",
    description: "Matamos a sua fome",
    logo_url: null, // TODO: Adicionar URL da imagem do logo Sanducha
    logo_icon: "chef-hat", // Ícone temporário até ter a imagem real
    display_order: 1,
  },
  {
    id: "wifi-express",
    name: "WiFi Express",
    description: tAuto("auto.components_home_partnerssection.1", "Internet de alta velocidade"),
    logo_url: null,
    logo_icon: "wifi",
    display_order: 2,
  },
];

const PartnersSection = () => {
  const { t } = useTranslation();
  const [dbPartners, setDbPartners] = useState<Partner[]>([]);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const scrollPos = useRef(0);

  useEffect(() => {
    const load = async () => {
      const [{ data: partnersData }, { data: settingData }] = await Promise.all([
        supabase
          .from("partners" as any)
          .select("id, name, description, logo_url, logo_icon, display_order")
          .eq("is_active", true)
          .order("display_order"),
        supabase
          .from("platform_settings")
          .select("value")
          .eq("key", "partners_section_enabled")
          .single(),
      ]);
      if (partnersData) setDbPartners(partnersData as any);
      if (settingData) setSectionEnabled(settingData.value === true || settingData.value === "true");
    };
    load();
  }, []);

  // Combinar parceiros padrão com parceiros do banco de dados
  const partners = useMemo(() => {
    // Translate default partners dynamically
    const defaultPartnersWithTranslations = DEFAULT_PARTNERS.map(p => ({
      ...p,
      description: t(`partners.defaults.${p.id}.description`) || p.description
    }));
    
    // Filtrar parceiros padrão que já existem no DB (evitar duplicados)
    const dbIds = new Set(dbPartners.map(p => p.name.toLowerCase()));
    const uniqueDefaults = defaultPartnersWithTranslations.filter(
      p => !dbIds.has(p.name.toLowerCase())
    );
    return [...uniqueDefaults, ...dbPartners].sort((a, b) => a.display_order - b.display_order);
  }, [dbPartners, t]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || partners.length === 0) return;

    const speed = 0.5; // px per frame

    const animate = () => {
      if (!isPaused && el) {
        scrollPos.current += speed;
        // Loop: the content is duplicated, so reset at half
        const halfWidth = el.scrollWidth / 2;
        if (scrollPos.current >= halfWidth) {
          scrollPos.current = 0;
        }
        el.scrollLeft = scrollPos.current;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPaused, partners]);

  if (!sectionEnabled || partners.length === 0) return null;

  // Duplicate for infinite scroll
  const displayPartners = [...partners, ...partners];

  return (
    <section className="relative py-8 md:py-10 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-partners" />
      <div className="absolute inset-0 bg-white/40" />

      {/* Glow effects */}
      <div className="absolute top-0 left-1/3 w-[520px] h-[320px] bg-[hsl(var(--bera-gold))]/18 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[420px] h-[260px] bg-[hsl(var(--bera-orange))]/10 rounded-full blur-[110px] pointer-events-none" />

      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-5"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--bera-gold))] animate-pulse" />
            <span className="text-[hsl(var(--bera-earth))]/60 text-xs font-medium tracking-wider uppercase">{t('partners.badge') || "Ecossistema"}</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[hsl(var(--bera-earth))] tracking-tight">
            {t('partners.title') || "Parceiros de Confiança"}
          </h2>
          <p className="text-[hsl(var(--bera-earth))]/60 text-sm mt-2 max-w-md mx-auto">
            {t('partners.subtitle') || "Empresas que fortalecem a experiência Berastays"}
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-[hsl(var(--bera-cream))] via-[hsl(var(--bera-cream))]/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-[hsl(var(--bera-cream))] via-[hsl(var(--bera-cream))]/80 to-transparent z-10 pointer-events-none" />

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {displayPartners.map((partner, i) => (
              <PartnerCard key={`${partner.id}-${i}`} partner={partner} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
